import type { WakeSignalKind } from "./wake-signal-types";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;

export type WakeSignalObservedKind = WakeSignalKind | "unknown";
export type WakeSignalListenerStatus =
  | "idle"
  | "connecting"
  | "listening"
  | "closed"
  | "disabled"
  | "error";

export type WakeSignalDiagnosticsSnapshot = {
  channel: string | null;
  listenerStatus: WakeSignalListenerStatus;
  allowedKinds: WakeSignalKind[];
  receivedTotal: Partial<Record<WakeSignalObservedKind, number>>;
  malformedTotal: number;
  filteredTotal: number;
  handlerFailuresTotal: number;
  listenReadyTotal: number;
  reconnectTotal: number;
  lastReceivedAt: string | null;
  lastListenAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  notificationQueueUsage: number | null;
  notificationQueueUsageError: string | null;
};

export type WakeSignalDiagnostics = {
  setAllowedKinds(kinds: readonly WakeSignalKind[]): void;
  markConnecting(): void;
  markListening(): void;
  markClosed(): void;
  markError(error: unknown): void;
  recordReceived(kind: WakeSignalObservedKind, malformed: boolean): void;
  recordFiltered(): void;
  recordHandlerFailure(error: unknown): void;
  getSnapshot(): Promise<WakeSignalDiagnosticsSnapshot>;
};

type NotificationQueueUsageRow = {
  queue_usage?: unknown;
  pg_notification_queue_usage?: unknown;
};

function rowsFromUnknown<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  return ((result as { rows?: T[] } | null)?.rows ?? []) as T[];
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function nowIso(): string {
  return new Date().toISOString();
}

function incrementCounter(
  counters: Partial<Record<WakeSignalObservedKind, number>>,
  kind: WakeSignalObservedKind,
): void {
  counters[kind] = (counters[kind] ?? 0) + 1;
}

async function readNotificationQueueUsage(rawSql: RawSql | undefined): Promise<{
  usage: number | null;
  error: string | null;
}> {
  if (!rawSql) return { usage: null, error: null };

  try {
    const rows = rowsFromUnknown<NotificationQueueUsageRow>(
      await rawSql(`SELECT pg_notification_queue_usage() AS queue_usage`),
    );
    const rawValue = rows[0]?.queue_usage ?? rows[0]?.pg_notification_queue_usage;
    const usage = typeof rawValue === "number" ? rawValue : Number(rawValue);
    return { usage: Number.isFinite(usage) ? usage : null, error: null };
  } catch (error) {
    return { usage: null, error: errorMessage(error).slice(0, 160) };
  }
}

export function createDisabledWakeSignalDiagnostics(
  reason: "disabled" | "database_url_missing" | "unsupported" = "disabled",
): WakeSignalDiagnosticsSnapshot {
  return {
    channel: null,
    listenerStatus: "disabled",
    allowedKinds: [],
    receivedTotal: {},
    malformedTotal: 0,
    filteredTotal: 0,
    handlerFailuresTotal: 0,
    listenReadyTotal: 0,
    reconnectTotal: 0,
    lastReceivedAt: null,
    lastListenAt: null,
    lastErrorAt: null,
    lastError: reason,
    notificationQueueUsage: null,
    notificationQueueUsageError: null,
  };
}

export function createWakeSignalDiagnostics(params: {
  channel: string;
  rawSql?: RawSql;
  initialStatus?: WakeSignalListenerStatus;
}): WakeSignalDiagnostics {
  let listenerStatus = params.initialStatus ?? "idle";
  let allowedKinds: WakeSignalKind[] = [];
  const receivedTotal: Partial<Record<WakeSignalObservedKind, number>> = {};
  let malformedTotal = 0;
  let filteredTotal = 0;
  let handlerFailuresTotal = 0;
  let listenReadyTotal = 0;
  let reconnectTotal = 0;
  let lastReceivedAt: string | null = null;
  let lastListenAt: string | null = null;
  let lastErrorAt: string | null = null;
  let lastError: string | null = null;

  return {
    setAllowedKinds(kinds) {
      allowedKinds = [...new Set([...allowedKinds, ...kinds])];
    },
    markConnecting() {
      listenerStatus = "connecting";
      lastError = null;
      lastErrorAt = null;
    },
    markListening() {
      listenerStatus = "listening";
      listenReadyTotal += 1;
      if (listenReadyTotal > 1) reconnectTotal += 1;
      lastListenAt = nowIso();
      lastError = null;
      lastErrorAt = null;
    },
    markClosed() {
      listenerStatus = "closed";
    },
    markError(error) {
      listenerStatus = "error";
      lastError = errorMessage(error).slice(0, 160);
      lastErrorAt = nowIso();
    },
    recordReceived(kind, malformed) {
      incrementCounter(receivedTotal, kind);
      if (malformed) malformedTotal += 1;
      lastReceivedAt = nowIso();
    },
    recordFiltered() {
      filteredTotal += 1;
    },
    recordHandlerFailure(error) {
      handlerFailuresTotal += 1;
      lastError = errorMessage(error).slice(0, 160);
      lastErrorAt = nowIso();
    },
    async getSnapshot() {
      const queueUsage = await readNotificationQueueUsage(params.rawSql);
      return {
        channel: params.channel,
        listenerStatus,
        allowedKinds,
        receivedTotal: { ...receivedTotal },
        malformedTotal,
        filteredTotal,
        handlerFailuresTotal,
        listenReadyTotal,
        reconnectTotal,
        lastReceivedAt,
        lastListenAt,
        lastErrorAt,
        lastError,
        notificationQueueUsage: queueUsage.usage,
        notificationQueueUsageError: queueUsage.error,
      };
    },
  };
}
